// カスタムルール設定画面

import { render } from './components';
import { 
  CustomGameConfig, 
  DEFAULT_CUSTOM_CONFIG, 
  RULE_PRESETS, 
  RulePreset,
  validateCustomConfig,
  mergeCustomConfig,
  getPresetConfig,
} from '../types';

export interface CustomRulesCallbacks {
  onConfirm: (config: CustomGameConfig) => void;
  onCancel: () => void;
}

// 現在の設定状態
let currentConfig: CustomGameConfig = { ...DEFAULT_CUSTOM_CONFIG };
let selectedPresetId: string = 'standard';
let showAdvanced: boolean = false;

// スタイル注入
function injectCustomRulesStyles(): void {
  if (document.getElementById('custom-rules-styles')) return;

  const style = document.createElement('style');
  style.id = 'custom-rules-styles';
  style.textContent = `
    .custom-rules-container {
      min-height: 100vh;
      min-height: 100dvh;
      background: linear-gradient(180deg, #0a0a1a 0%, #1a1a2e 50%, #0f0f23 100%);
      padding: 16px;
      overflow-y: auto;
    }
    
    .rules-header {
      text-align: center;
      margin-bottom: 24px;
    }
    
    .rules-title {
      color: #ffd700;
      font-size: clamp(1.5rem, 5vw, 2rem);
      margin-bottom: 8px;
      text-shadow: 0 0 20px rgba(255, 215, 0, 0.3);
    }
    
    .rules-subtitle {
      color: #888;
      font-size: 0.9rem;
    }
    
    .preset-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
      gap: 12px;
      margin-bottom: 24px;
    }
    
    .preset-card {
      background: rgba(0,0,0,0.3);
      border: 2px solid rgba(255,255,255,0.1);
      border-radius: 12px;
      padding: 16px;
      cursor: pointer;
      transition: all 0.2s;
      text-align: center;
    }
    
    .preset-card:hover {
      border-color: rgba(255,255,255,0.3);
      transform: translateY(-2px);
    }
    
    .preset-card.selected {
      border-color: #ffd700;
      background: rgba(255, 215, 0, 0.1);
      box-shadow: 0 0 20px rgba(255, 215, 0, 0.2);
    }
    
    .preset-icon {
      font-size: 2rem;
      margin-bottom: 8px;
    }
    
    .preset-name {
      color: #fff;
      font-weight: bold;
      font-size: 0.9rem;
      margin-bottom: 4px;
    }
    
    .preset-desc {
      color: #888;
      font-size: 0.7rem;
      line-height: 1.3;
    }
    
    .settings-section {
      background: rgba(0,0,0,0.3);
      border-radius: 12px;
      padding: 20px;
      margin-bottom: 16px;
    }
    
    .section-title {
      color: #00d4ff;
      font-size: 1rem;
      font-weight: bold;
      margin-bottom: 16px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .settings-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 16px;
    }
    
    .setting-item {
      background: rgba(255,255,255,0.03);
      border-radius: 8px;
      padding: 12px;
    }
    
    .setting-label {
      color: #aaa;
      font-size: 0.8rem;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      justify-content: space-between;
    }
    
    .setting-value {
      color: #fff;
      font-size: 1.1rem;
      font-weight: bold;
    }
    
    .setting-input {
      width: 100%;
      background: rgba(0,0,0,0.3);
      border: 1px solid rgba(255,255,255,0.2);
      border-radius: 6px;
      padding: 8px 12px;
      color: #fff;
      font-size: 1rem;
    }
    
    .setting-input:focus {
      outline: none;
      border-color: #00d4ff;
    }
    
    .setting-slider {
      width: 100%;
      margin-top: 8px;
    }
    
    .toggle-switch {
      position: relative;
      width: 50px;
      height: 26px;
      background: rgba(255,255,255,0.1);
      border-radius: 13px;
      cursor: pointer;
      transition: background 0.2s;
    }
    
    .toggle-switch.active {
      background: #2ecc71;
    }
    
    .toggle-switch::after {
      content: '';
      position: absolute;
      width: 22px;
      height: 22px;
      background: #fff;
      border-radius: 50%;
      top: 2px;
      left: 2px;
      transition: transform 0.2s;
    }
    
    .toggle-switch.active::after {
      transform: translateX(24px);
    }
    
    .advanced-toggle {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 8px;
      color: #888;
      font-size: 0.85rem;
      cursor: pointer;
      padding: 12px;
      margin-bottom: 16px;
    }
    
    .advanced-toggle:hover {
      color: #fff;
    }
    
    .tier-config {
      margin-top: 16px;
    }
    
    .tier-row {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 8px;
      background: rgba(0,0,0,0.2);
      border-radius: 6px;
      margin-bottom: 6px;
    }
    
    .tier-round {
      color: #888;
      font-size: 0.8rem;
      width: 60px;
    }
    
    .tier-inputs {
      display: flex;
      gap: 8px;
      flex: 1;
    }
    
    .tier-input {
      flex: 1;
      text-align: center;
    }
    
    .tier-input input {
      width: 40px;
      background: rgba(255,255,255,0.1);
      border: 1px solid;
      border-radius: 4px;
      padding: 4px;
      color: #fff;
      text-align: center;
      font-size: 0.9rem;
    }
    
    .tier-a input { border-color: #2ecc71; }
    .tier-b input { border-color: #3498db; }
    .tier-c input { border-color: #9b59b6; }
    
    .tier-label {
      font-size: 0.7rem;
      margin-top: 2px;
    }
    
    .tier-a .tier-label { color: #2ecc71; }
    .tier-b .tier-label { color: #3498db; }
    .tier-c .tier-label { color: #9b59b6; }
    
    .rules-actions {
      display: flex;
      gap: 12px;
      justify-content: center;
      margin-top: 24px;
      padding-bottom: 24px;
    }
    
    .rules-btn {
      padding: 14px 32px;
      border-radius: 8px;
      font-size: 1rem;
      font-weight: bold;
      cursor: pointer;
      transition: all 0.2s;
      min-width: 140px;
    }
    
    .rules-btn-primary {
      background: linear-gradient(135deg, #ffd700, #ffaa00);
      border: none;
      color: #000;
    }
    
    .rules-btn-primary:hover {
      transform: scale(1.05);
      box-shadow: 0 4px 20px rgba(255, 215, 0, 0.4);
    }
    
    .rules-btn-secondary {
      background: transparent;
      border: 2px solid #666;
      color: #888;
    }
    
    .rules-btn-secondary:hover {
      border-color: #fff;
      color: #fff;
    }
    
    .validation-error {
      color: #e74c3c;
      font-size: 0.85rem;
      background: rgba(231, 76, 60, 0.1);
      border: 1px solid rgba(231, 76, 60, 0.3);
      border-radius: 8px;
      padding: 12px;
      margin-bottom: 16px;
    }
    
    .config-summary {
      background: rgba(0,0,0,0.2);
      border-radius: 8px;
      padding: 12px;
      margin-top: 16px;
      font-size: 0.8rem;
      color: #888;
    }
    
    .config-summary-item {
      display: flex;
      justify-content: space-between;
      padding: 4px 0;
      border-bottom: 1px solid rgba(255,255,255,0.05);
    }
    
    .config-summary-item:last-child {
      border-bottom: none;
    }
    
    .config-summary-value {
      color: #fff;
    }
  `;
  document.head.appendChild(style);
}

// メイン描画
export function renderCustomRulesScreen(callbacks: CustomRulesCallbacks): void {
  injectCustomRulesStyles();
  
  const validation = validateCustomConfig(currentConfig);

  render(`
    <div class="custom-rules-container">
      <div class="rules-header">
        <h1 class="rules-title">⚙️ カスタムルール</h1>
        <p class="rules-subtitle">ゲームルールをカスタマイズ</p>
      </div>

      <!-- プリセット選択 -->
      <div class="settings-section">
        <div class="section-title">📋 プリセット</div>
        <div class="preset-grid">
          ${RULE_PRESETS.map((preset) => renderPresetCard(preset)).join('')}
        </div>
      </div>

      <!-- バリデーションエラー -->
      ${!validation.valid ? `
        <div class="validation-error">
          <strong>⚠️ 設定エラー</strong><br>
          ${validation.errors.join('<br>')}
        </div>
      ` : ''}

      <!-- 基本設定 -->
      <div class="settings-section">
        <div class="section-title">🎮 基本設定</div>
        <div class="settings-grid">
          ${renderNumberSetting('qualifyingRounds', '予選ラウンド数', currentConfig.qualifyingRounds, 1, 10)}
          ${renderNumberSetting('playerCount', 'プレイヤー数', currentConfig.playerCount, 2, 8)}
          ${renderNumberSetting('maxLosses', '敗退条件 (敗北数)', currentConfig.maxLosses, 1, 5)}
        </div>
      </div>

      <!-- デッキ設定 -->
      <div class="settings-section">
        <div class="section-title">🃏 デッキ設定</div>
        <div class="settings-grid">
          ${renderNumberSetting('startingDeckSize', '初期デッキ枚数', currentConfig.startingDeckSize, 4, 10)}
          ${renderNumberSetting('maxDeckSize', 'デッキ上限', currentConfig.maxDeckSize, 15, 50)}
          ${renderNumberSetting('benchLimit', 'ベンチ上限', currentConfig.benchLimit, 4, 10)}
        </div>
      </div>

      <!-- 詳細設定トグル -->
      <div class="advanced-toggle" id="advancedToggle">
        <span>${showAdvanced ? '▼' : '▶'}</span>
        <span>詳細設定</span>
      </div>

      <!-- 詳細設定 -->
      <div id="advancedSettings" style="display: ${showAdvanced ? 'block' : 'none'};">
        <!-- バトル設定 -->
        <div class="settings-section">
          <div class="section-title">⚔️ バトル設定</div>
          <div class="settings-grid">
            ${renderToggleSetting('enableAbilities', '能力を有効', currentConfig.enableAbilities)}
            ${renderToggleSetting('enableSynergies', 'シナジーを有効', currentConfig.enableSynergies)}
            ${renderToggleSetting('allowCardRemoval', 'カード削除を許可', currentConfig.allowCardRemoval)}
            ${renderToggleSetting('showOpponentDeck', '相手デッキを公開', currentConfig.showOpponentDeck)}
          </div>
        </div>

        <!-- ファン設定 -->
        <div class="settings-section">
          <div class="section-title">⭐ ファン設定</div>
          <div class="settings-grid">
            ${renderNumberSetting('baseFansPerWin', '勝利時基本ファン', currentConfig.baseFansPerWin, 1, 10)}
            ${renderNumberSetting('bonusFansForStreak', '連勝ボーナス', currentConfig.bonusFansForStreak, 0, 5)}
          </div>
        </div>

        <!-- ティア取得設定 -->
        <div class="settings-section">
          <div class="section-title">📦 ティア取得設定</div>
          <p style="color: #888; font-size: 0.8rem; margin-bottom: 12px;">
            各ラウンドで取得できるカードのティア別枚数
          </p>
          <div class="tier-config">
            ${renderTierConfig()}
          </div>
        </div>
      </div>

      <!-- 設定サマリー -->
      <div class="settings-section">
        <div class="section-title">📊 現在の設定</div>
        <div class="config-summary">
          ${renderConfigSummary()}
        </div>
      </div>

      <!-- アクションボタン -->
      <div class="rules-actions">
        <button class="rules-btn rules-btn-secondary" id="btnCancelRules">キャンセル</button>
        <button class="rules-btn rules-btn-primary" id="btnConfirmRules" ${!validation.valid ? 'disabled' : ''}>
          ✓ この設定で開始
        </button>
      </div>
    </div>
  `);

  // イベントバインド
  bindEvents(callbacks);
}

// プリセットカード描画
function renderPresetCard(preset: RulePreset): string {
  const isSelected = selectedPresetId === preset.id;
  return `
    <div class="preset-card ${isSelected ? 'selected' : ''}" data-preset="${preset.id}">
      <div class="preset-icon">${preset.icon}</div>
      <div class="preset-name">${preset.name}</div>
      <div class="preset-desc">${preset.description}</div>
    </div>
  `;
}

// 数値設定描画
function renderNumberSetting(
  key: string,
  label: string,
  value: number,
  min: number,
  max: number
): string {
  return `
    <div class="setting-item">
      <div class="setting-label">
        <span>${label}</span>
        <span class="setting-value">${value}</span>
      </div>
      <input type="range" 
             class="setting-slider" 
             data-key="${key}"
             min="${min}" 
             max="${max}" 
             value="${value}">
    </div>
  `;
}

// トグル設定描画
function renderToggleSetting(key: string, label: string, value: boolean): string {
  return `
    <div class="setting-item">
      <div class="setting-label">
        <span>${label}</span>
        <div class="toggle-switch ${value ? 'active' : ''}" data-key="${key}"></div>
      </div>
    </div>
  `;
}

// ティア設定描画
function renderTierConfig(): string {
  return currentConfig.tierDraws.map((draws, idx) => `
    <div class="tier-row">
      <div class="tier-round">R${idx + 1}</div>
      <div class="tier-inputs">
        <div class="tier-input tier-a">
          <input type="number" min="0" max="5" value="${draws.A}" data-round="${idx}" data-tier="A">
          <div class="tier-label">Tier A</div>
        </div>
        <div class="tier-input tier-b">
          <input type="number" min="0" max="5" value="${draws.B}" data-round="${idx}" data-tier="B">
          <div class="tier-label">Tier B</div>
        </div>
        <div class="tier-input tier-c">
          <input type="number" min="0" max="5" value="${draws.C}" data-round="${idx}" data-tier="C">
          <div class="tier-label">Tier C</div>
        </div>
      </div>
    </div>
  `).join('');
}

// 設定サマリー描画
function renderConfigSummary(): string {
  const totalCards = currentConfig.tierDraws.reduce((sum, draws) => 
    sum + draws.A + draws.B + draws.C, 0
  );
  
  return `
    <div class="config-summary-item">
      <span>ゲーム形式</span>
      <span class="config-summary-value">${currentConfig.playerCount}人 / ${currentConfig.qualifyingRounds}ラウンド</span>
    </div>
    <div class="config-summary-item">
      <span>敗退条件</span>
      <span class="config-summary-value">${currentConfig.maxLosses}敗</span>
    </div>
    <div class="config-summary-item">
      <span>デッキ</span>
      <span class="config-summary-value">初期${currentConfig.startingDeckSize}枚 / 上限${currentConfig.maxDeckSize}枚</span>
    </div>
    <div class="config-summary-item">
      <span>取得カード総数</span>
      <span class="config-summary-value">${totalCards}枚</span>
    </div>
    <div class="config-summary-item">
      <span>能力</span>
      <span class="config-summary-value">${currentConfig.enableAbilities ? 'ON' : 'OFF'}</span>
    </div>
    <div class="config-summary-item">
      <span>シナジー</span>
      <span class="config-summary-value">${currentConfig.enableSynergies ? 'ON' : 'OFF'}</span>
    </div>
  `;
}

// イベントバインド
function bindEvents(callbacks: CustomRulesCallbacks): void {
  // プリセット選択
  document.querySelectorAll('.preset-card').forEach((card) => {
    card.addEventListener('click', () => {
      const presetId = card.getAttribute('data-preset');
      if (presetId) {
        selectedPresetId = presetId;
        currentConfig = getPresetConfig(presetId);
        renderCustomRulesScreen(callbacks);
      }
    });
  });

  // 数値スライダー
  document.querySelectorAll('.setting-slider').forEach((slider) => {
    slider.addEventListener('input', (e) => {
      const key = (e.target as HTMLInputElement).getAttribute('data-key') as keyof CustomGameConfig;
      const value = parseInt((e.target as HTMLInputElement).value);
      if (key && !isNaN(value)) {
        (currentConfig as unknown as Record<string, unknown>)[key] = value;
        
        // ラウンド数変更時はティア設定も調整
        if (key === 'qualifyingRounds') {
          adjustTierDraws(value);
        }
        
        selectedPresetId = 'custom';
        renderCustomRulesScreen(callbacks);
      }
    });
  });

  // トグル
  document.querySelectorAll('.toggle-switch').forEach((toggle) => {
    toggle.addEventListener('click', () => {
      const key = toggle.getAttribute('data-key') as keyof CustomGameConfig;
      if (key) {
        (currentConfig as unknown as Record<string, unknown>)[key] = !(currentConfig as unknown as Record<string, boolean>)[key];
        selectedPresetId = 'custom';
        renderCustomRulesScreen(callbacks);
      }
    });
  });

  // ティア入力
  document.querySelectorAll('.tier-input input').forEach((input) => {
    input.addEventListener('change', (e) => {
      const roundIdx = parseInt((e.target as HTMLInputElement).getAttribute('data-round') ?? '0');
      const tier = (e.target as HTMLInputElement).getAttribute('data-tier') as 'A' | 'B' | 'C';
      const value = parseInt((e.target as HTMLInputElement).value);
      
      if (!isNaN(roundIdx) && tier && !isNaN(value)) {
        currentConfig.tierDraws[roundIdx][tier] = Math.max(0, Math.min(5, value));
        selectedPresetId = 'custom';
        renderCustomRulesScreen(callbacks);
      }
    });
  });

  // 詳細設定トグル
  document.getElementById('advancedToggle')?.addEventListener('click', () => {
    showAdvanced = !showAdvanced;
    renderCustomRulesScreen(callbacks);
  });

  // キャンセル
  document.getElementById('btnCancelRules')?.addEventListener('click', () => {
    resetToDefault();
    callbacks.onCancel();
  });

  // 確定
  document.getElementById('btnConfirmRules')?.addEventListener('click', () => {
    const validation = validateCustomConfig(currentConfig);
    if (validation.valid) {
      callbacks.onConfirm({ ...currentConfig });
    }
  });
}

// ティア設定を調整
function adjustTierDraws(rounds: number): void {
  while (currentConfig.tierDraws.length < rounds) {
    // 最後の設定をコピー
    const last = currentConfig.tierDraws[currentConfig.tierDraws.length - 1];
    currentConfig.tierDraws.push({ ...last });
  }
  while (currentConfig.tierDraws.length > rounds) {
    currentConfig.tierDraws.pop();
  }
}

// デフォルトにリセット
export function resetToDefault(): void {
  currentConfig = { 
    ...DEFAULT_CUSTOM_CONFIG, 
    tierDraws: DEFAULT_CUSTOM_CONFIG.tierDraws.map((t) => ({ ...t })) 
  };
  selectedPresetId = 'standard';
  showAdvanced = false;
}

// 現在の設定を取得
export function getCurrentConfig(): CustomGameConfig {
  return { ...currentConfig };
}
